import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import { company } from "../mock/mock";

// Scroll to top on route change
export const ScrollToTop = ({ pathname }) => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [pathname]);
  return null;
};

// Section heading with eyebrow
export const SectionHeading = ({ eyebrow, title, subtitle, center, light }) => (
  <div className={`${center ? "text-center mx-auto" : ""} max-w-3xl mb-12`}>
    {eyebrow && (
      <div className={`section-eyebrow mb-3 ${light ? "text-gold-light" : "text-gold-dark"}`}>
        {eyebrow}
      </div>
    )}
    <h2
      className={`font-heading font-extrabold text-[28px] md:text-[38px] leading-tight ${
        light ? "text-white" : "text-navy"
      }`}
    >
      {title}
    </h2>
    {subtitle && (
      <p className={`mt-4 text-[15px] md:text-base leading-relaxed ${light ? "text-white/70" : "text-muted-foreground"}`}>
        {subtitle}
      </p>
    )}
  </div>
);

// Primary button
export const PrimaryButton = ({ to, href, children, className = "", gold }) => {
  const cls = `inline-flex items-center gap-2 rounded-md px-6 py-3.5 text-sm font-bold transition-all shadow-sm hover:shadow-md ${
    gold
      ? "bg-gold text-navy-dark hover:bg-gold-light"
      : "bg-navy text-white hover:bg-navy-light"
  } ${className}`;
  if (href) return <a href={href} className={cls}>{children}<ArrowRight size={16} /></a>;
  return <Link to={to} className={cls}>{children}<ArrowRight size={16} /></Link>;
};

// Reusable page hero (inner pages)
export const PageHero = ({ eyebrow, title, subtitle, image }) => (
  <section className="relative brand-gradient overflow-hidden">
    {image && (
      <div className="absolute inset-0">
        <img src={image} alt="" className="w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 brand-gradient opacity-80" />
      </div>
    )}
    <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28">
      <div className="max-w-3xl fade-up">
        {eyebrow && <div className="section-eyebrow text-gold-light mb-3">{eyebrow}</div>}
        <h1 className="font-heading font-extrabold text-white text-[32px] md:text-[48px] leading-[1.1]">
          {title}
        </h1>
        {subtitle && <p className="mt-5 text-white/80 text-base md:text-lg leading-relaxed">{subtitle}</p>}
      </div>
    </div>
  </section>
);

// Marquee tagline strip
export const TaglineStrip = () => (
  <div className="bg-gold py-3 overflow-hidden">
    <div className="animate-marquee inline-block">
      {Array.from({ length: 2 }).map((_, i) => (
        <span key={i} className="text-navy-dark font-heading font-bold text-sm uppercase tracking-wider">
          {Array.from({ length: 6 }).map((_, j) => (
            <span key={j} className="mx-6">Outsource your Call Centre Needs &nbsp;•</span>
          ))}
        </span>
      ))}
    </div>
  </div>
);

// Bottom CTA banner
export const CTABanner = ({ title, subtitle }) => (
  <section className="brand-gradient">
    <div className="max-w-7xl mx-auto px-6 py-16 md:py-20 text-center">
      <h2 className="font-heading font-extrabold text-white text-[26px] md:text-[36px] leading-tight max-w-3xl mx-auto">
        {title || "Ready to lower costs and grow revenue?"}
      </h2>
      <p className="mt-4 text-white/75 max-w-xl mx-auto">
        {subtitle || "Give us 30 days. Fill in the inquiry form and our Client Engagement Team will contact you within 24 hours."}
      </p>
      <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
        <PrimaryButton to="/contact" gold>Book Free Consultation</PrimaryButton>
        <a
          href={`tel:${company.phoneRaw}`}
          className="inline-flex items-center gap-2 rounded-md border border-white/30 px-6 py-3.5 text-sm font-bold text-white hover:bg-white/10 transition-colors"
        >
          <Phone size={16} /> {company.phone}
        </a>
      </div>
    </div>
  </section>
);

// Cookie consent banner
export const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const accepted = localStorage.getItem("mabm_cookie");
    if (!accepted) setVisible(true);
  }, []);
  const handle = (val) => {
    localStorage.setItem("mabm_cookie", val);
    setVisible(false);
  };
  if (!visible) return null;
  return (
    <div className="fixed bottom-4 right-4 left-4 md:left-auto md:max-w-sm z-[60] bg-white rounded-xl shadow-2xl border border-border p-5 fade-up">
      <h4 className="font-heading font-bold text-navy text-sm mb-1">This website uses cookies.</h4>
      <p className="text-xs text-muted-foreground leading-relaxed mb-4">
        We use cookies to analyze website traffic and optimize your website experience. By accepting,
        your data will be aggregated with all other user data.
      </p>
      <div className="flex gap-3">
        <button
          onClick={() => handle("declined")}
          className="flex-1 rounded-md border border-border py-2 text-xs font-bold text-navy hover:bg-secondary transition-colors"
        >
          Decline
        </button>
        <button
          onClick={() => handle("accepted")}
          className="flex-1 rounded-md bg-navy py-2 text-xs font-bold text-white hover:bg-navy-light transition-colors"
        >
          Accept
        </button>
      </div>
    </div>
  );
};
